<template>
  <section class="grid grid-rows-[auto,auto,6fr,4fr] h-full grid-cols-[2fr, 1fr] pb-2">
    <!-- 1st Row -->
    <div
      class="flex items-center justify-between col-span-2 row-span-1 px-2 py-2 bg-white border-t border-b">
      <div class="flex items-center justify-start flex-grow-0 w-full gap-5">
        <button @click="onGoBack" class="p-2 rounded-lg text-theme-600 hover:bg-theme-100">
          <IconArrowLeft />
        </button>
        <p class="font-semibold uppercase text-md">{{ $t('competitorMaterials.match') }}</p>
      </div>
      <div
        v-if="modifiedCompetitorMaterials.length > 0"
        class="flex items-center justify-center gap-2">
        <BaseButton size="xs" outline class="gap-2 capitalize" @click="onCancelForm">
          {{ $t('common.actions.cancel') }}
        </BaseButton>
        <BaseButton size="xs" class="gap-2 capitalize" @click="onSaveForm">
          {{ $t('common.actions.save') }}
        </BaseButton>
      </div>
    </div>

    <!-- 2nd Row -->
    <div class="col-span-1 row-span-1 p-2 bg-white border-b border-r">
      <h2 class="flex items-center justify-start gap-2 font-medium text-gray-800">
        <span
          class="inline-flex items-center justify-center text-sm font-medium rounded-full size-6 text-theme-800 bg-theme-100"
          >1</span
        >
        {{ $t('competitorMaterials.selectMaterial') }}
      </h2>
    </div>
    <div class="col-span-1 row-span-1 p-2 bg-white border-b">
      <h2 class="flex items-center justify-start gap-2 font-medium text-gray-800">
        <span
          class="inline-flex items-center justify-center text-sm font-medium rounded-full size-6 text-theme-800 bg-theme-100"
          >2</span
        >
        {{ $t('competitorMaterials.selectCompetitor') }}
      </h2>
    </div>

    <!-- 3rd Row -->
    <div class="col-span-1 row-span-1 overflow-x-hidden overflow-y-auto bg-white border-r">
      <BaseTable
        v-if="materials"
        :headers="materialsHeaders"
        :list="materials"
        :total-records="materialsTotalCount"
        :selected-item="selectedMaterial"
        table-id="materials-management"
        only-search
        is-add-new-hidden
        :is-search-hidden="!!route.query.id"
        :clickable="!route.query.id"
        disable-multiselect
        @refresh-data="onRefreshDataMaterials"
        @table-item-clicked="onSelectMaterial">
      </BaseTable>
    </div>

    <div class="col-span-1 row-span-1 overflow-x-hidden overflow-y-auto bg-white">
      <BaseTable
        v-if="filteredCompetitors"
        :headers="competitorsHeaders"
        :list="filteredCompetitors"
        :selected-item="selectedCompetitor"
        table-id="materials-competitors"
        is-add-new-hidden
        only-search
        clickable
        disable-multiselect
        @refresh-data="onRefreshDataCompetitors"
        @table-item-clicked="onSelectCompetitor">
      </BaseTable>
    </div>

    <!-- 4th Row -->
    <div
      class="h-full col-span-2 row-span-1 pb-6 overflow-x-hidden overflow-y-hidden bg-white border-t"
      :class="isDisabledCompetitorMaterial ? ' pointer-events-none' : ' pointer-events-auto'">
      <h2
        class="flex items-center justify-center gap-2 py-2 font-medium text-center bg-white border border-b"
        :class="isDisabledCompetitorMaterial ? 'text-gray-300' : 'text-gray-800'">
        <span
          class="inline-flex items-center justify-center text-sm font-normal rounded-full size-6"
          :class="
            isDisabledCompetitorMaterial
              ? 'text-gray-300 bg-gray-100'
              : 'text-theme-800 bg-theme-100'
          "
          >3</span
        >
        {{ $t('competitorMaterials.competitorMaterial') }}
      </h2>

      <BaseTable
        v-if="competitorMaterials"
        :headers="competitorMaterialsHeaders"
        :list="filteredCompetitorMaterials"
        :total-records="competitorMaterialsTotalCount"
        table-id="materials-competitors"
        hide-grouping
        :is-add-new-hidden="isDisabledCompetitorMaterial"
        :is-search-hidden="isDisabledCompetitorMaterial"
        is-saving-layout-hidden
        is-download-button-hidden
        is-refresh-button-hidden
        clickable
        @table-item-clicked="onSelectCompetitorMaterial"
        @refresh-data="onGetCompetitorMaterials"
        @add-new="onOpenModal"
        @deleteMultiselect="onDeleteMultiselect">
        <template #actions="{ item }">
          <div class="flex items-center justify-center gap-1">
            <button
              v-if="item.isFromManager"
              type="button"
              @click="onDeleteRow(item)"
              class="rounded-full p-1.5 text-red-400 hover:bg-red-50">
              <IconTrashOutline class="size-4" />
            </button>
          </div>
        </template>
      </BaseTable>
    </div>

    <CompetitorMaterialsWizardModal
      :competitor-alter-code="selectedCompetitorMaterial"
      :is-open="isModalOpen"
      :is-new="isNewAlterCode"
      @close="onCloseModal"
      @save="onSaveCompetitorMaterial" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />

    <BaseDrawerConfirm
      :is-active="confirmDrawerFormActive"
      @cancel="confirmDrawerFormActive = false"
      @confirm="onDiscardFormChanges" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import {
  addOrUpdateCompetitorMaterials,
  getCompetitorMaterialsWithConditions,
  getMaterialById,
  getMaterialsWithConditions,
} from '@/services/api/materials';
import { getCompetitors } from '@/services/api/traders';
import { filterData, sortData } from '@/utils/table';
import { v4 as uuidv4 } from 'uuid';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import CompetitorMaterialsWizardModal from '@/components/traders/competitorMaterials/CompetitorMaterialsWizardModal.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import IconArrowLeft from '@/assets/icons/IconArrowLeft.vue';

import type { CompetitorMaterial, Material } from '@/types/Materials';
import type { TableFilterData } from '@/types/Base';
import type { Competitor } from '@/types/Traders';
import { deepCopy } from '@/utils/data';
import { useI18n } from 'vue-i18n';

const materials = ref<Material[]>([]);
const materialsTotalCount = ref<number>();
const competitors = ref<Partial<Competitor>[]>([]);
const filteredCompetitors = ref<Partial<Competitor>[]>([]);
const competitorMaterials = ref<CompetitorMaterial[]>([]);
const competitorMaterialsTotalCount = ref<number>();
const filteredCompetitorMaterials = ref<CompetitorMaterial[]>([]);
const isDisabledCompetitorMaterial = ref(true);
const route = useRoute();

const { getAlterCodeTypesLookup, getMeasurementUnitsLookup } = useLookupsStore();

onMounted(async () => {
  competitorMaterials.value = [];
  filteredCompetitorMaterials.value = [];
  getAlterCodeTypesLookup();
  getMeasurementUnitsLookup();

  if (route.query.id as string) {
    const materialById = await getMaterialById(route.query.id as string);
    if (materialById) {
      materials.value?.push(materialById);
      selectedMaterial.value = materialById;
    }
  } else {
    const materialsRes = await getMaterialsWithConditions({
      itemsPerPage: 50,
      sortColumn: 'name',
      sortAscending: true,
      page: 1,
    });
    if (materialsRes) {
      materials.value = materialsRes.data;
      materialsTotalCount.value = materialsRes.count;
    }
  }

  const competitorsRes = await getCompetitors();
  if (competitorsRes) {
    competitors.value = competitorsRes;
    filteredCompetitors.value = deepCopy<Partial<Competitor>[]>(competitors.value);
  }
});

const { t: $t } = useI18n({ useScope: 'global' });
const materialsHeaders = [
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'smallCode', text: $t('material.smallCode'), type: 'text', active: true },
  { value: 'smallName', text: $t('material.smallName'), type: 'text', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
];

const competitorsHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'trader.name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'competitorType.name', text: $t('salesperson.type'), type: 'pill', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
];

const competitorMaterialsHeaders = [
  { value: 'material.name', text: 'Material name', type: 'text', active: true },
  { value: 'competitor.code', text: $t('material.competitor'), type: 'text', active: true },
  {
    value: 'materialAlterCode.alterCode.name',
    text: $t('material.alterCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'materialAlterCode.measurementUnit.name',
    text: $t('material.measurementUnit'),
    type: 'text',
    active: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'status', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'isDefault', text: $t('material.default'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

async function onRefreshDataMaterials(data: TableFilterData) {
  let itemsPerPage = data.itemsPerPage || 50;
  const isSorting = !!(data.column && data.order);

  if (data.page == 1 && selectedMaterial.value && !isSorting)
    itemsPerPage = data.itemsPerPage ? (itemsPerPage = data.itemsPerPage - 1) : 50;

  const sortColumn = data.column !== undefined ? materialsHeaders[data.column]?.value : 'code';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params = {
    page: data.page || 1,
    itemsPerPage,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
  };

  const res = await getMaterialsWithConditions(params);
  if (res) {
    materials.value = res.data;
    materialsTotalCount.value = res.count;
  }

  if (data.page == 1 && selectedMaterial.value && !isSorting) {
    materials.value?.unshift(selectedMaterial.value);
  }
}

function onRefreshDataCompetitors(data: TableFilterData) {
  filteredCompetitors.value = deepCopy<Partial<Competitor>[]>(competitors.value);
  if (!filteredCompetitors.value) return;
  if (data.query) {
    filteredCompetitors.value = filterData(filteredCompetitors.value, data.query, [
      'code',
      'competitorType.name',
    ]);
  }

  if (data.column !== undefined && data.order) {
    filteredCompetitors.value = sortData(
      filteredCompetitors.value,
      data.column,
      data.order,
      competitorsHeaders
    );
  }
}

const selectedMaterial = ref<Material>();

async function onGetCompetitorMaterials(data: TableFilterData) {
  let itemsPerPage = data.itemsPerPage || 50;

  const sortColumn =
    data.column !== undefined ? competitorMaterialsHeaders[data.column]?.value : 'material.name';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params = {
    page: data.page || 1,
    itemsPerPage,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    competitorId: selectedCompetitor.value?.id,
    materialId: selectedMaterial.value?.id,
  };

  const res = await getCompetitorMaterialsWithConditions(params);
  if (res) {
    competitorMaterials.value = res.data;
    competitorMaterialsTotalCount.value = res.count;
    filteredCompetitorMaterials.value = deepCopy<CompetitorMaterial[]>(competitorMaterials.value);
    if (competitorMaterials.value) isDisabledCompetitorMaterial.value = false;
  }
}

async function onSelectMaterial(item: Material) {
  if (route.query.id) return;
  selectedMaterial.value = item;
  if (selectedMaterial.value && selectedCompetitor.value) {
    onGetCompetitorMaterials({});
  }
}

const selectedCompetitor = ref<Competitor>();

async function onSelectCompetitor(item: Competitor) {
  selectedCompetitor.value = item;
  if (selectedCompetitor.value) {
    onGetCompetitorMaterials({});
  }
}

const { alterCodeTypes } = storeToRefs(useLookupsStore());
const isNewAlterCode = ref<boolean>(false);

function initializeCompetitorAlterCode(): CompetitorMaterial {
  const alterCodeId = uuidv4();
  return {
    id: uuidv4(),
    isActive: true,
    isFromManager: true,
    isDefault: true,
    competitor: selectedCompetitor.value || { code: '', id: '' },
    competitorId: selectedCompetitor.value?.id || '',
    materialId: selectedMaterial.value?.id,
    material: {
      id: selectedMaterial.value?.id,
      name: selectedMaterial.value?.name,
    },
    materialAlterCode: {
      id: uuidv4(),
      isActive: true,
      isFromManager: true,
      materialId: selectedMaterial.value?.id,
      alterCode: {
        id: alterCodeId,
        code: '',
        name: '',
        alterCodeTypeId: alterCodeTypes.value.find((type) => type.eType == 1)?.id || '',
        recordMode: 1,
      },
      alterCodeId,
      measurementUnitId: '',
      measurementUnit: { id: '', code: '', name: '' },
      recordMode: 1,
    },
    recordMode: 1,
  };
}
const selectedCompetitorMaterial = ref<CompetitorMaterial>(initializeCompetitorAlterCode());
const isModalOpen = ref(false);

function onOpenModal(item: CompetitorMaterial) {
  if (item) {
    isNewAlterCode.value = false;
    selectedCompetitorMaterial.value = item;
    selectedCompetitorMaterial.value.recordMode = 2;
    selectedCompetitorMaterial.value.materialAlterCode.alterCode.recordMode = 2;
  } else {
    selectedCompetitorMaterial.value = initializeCompetitorAlterCode();
    isNewAlterCode.value = true;
  }
  isModalOpen.value = true;
}

function onCloseModal() {
  isModalOpen.value = false;
  isNewAlterCode.value = false;
}

const modifiedCompetitorMaterials = ref<CompetitorMaterial[]>([]);

function onSaveCompetitorMaterial(item: CompetitorMaterial) {
  modifiedCompetitorMaterials.value.push(item);
  isModalOpen.value = false;
  isNewAlterCode.value = false;
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<CompetitorMaterial>();
function onDeleteRow(item: CompetitorMaterial) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onDeleteSingle() {
  if (!deleteRowItem.value) return;

  if (deleteRowItem.value.recordMode === 1) {
    const modifiedIdx = modifiedCompetitorMaterials.value.findIndex(
      (item) => item.id === deleteRowItem.value?.id
    );
    if (modifiedIdx !== -1) {
      modifiedCompetitorMaterials.value.splice(modifiedIdx, 1);
    }

    const filteredIdx = filteredCompetitorMaterials.value.findIndex(
      (item) => item.id === deleteRowItem.value?.id
    );
    if (filteredIdx !== -1) {
      filteredCompetitorMaterials.value.splice(filteredIdx, 1);
    }
  } else {
    deleteRowItem.value.recordMode = 3;
    modalDeleteRow.value = false;
    modifiedCompetitorMaterials.value.push(deleteRowItem.value);
  }

  modalDeleteRow.value = false;
}

function onDeleteMultiselect(items: CompetitorMaterial[]) {
  items.forEach((item) => {
    if (!item.isFromManager) return;

    if (item.recordMode === 1) {
      const modifiedIdx = modifiedCompetitorMaterials.value.findIndex(
        (modifiedItem) => modifiedItem.id === item.id
      );
      if (modifiedIdx !== -1) {
        modifiedCompetitorMaterials.value.splice(modifiedIdx, 1);
      }

      const filteredIdx = filteredCompetitorMaterials.value.findIndex(
        (filteredItem) => filteredItem.id === item.id
      );
      if (filteredIdx !== -1) {
        filteredCompetitorMaterials.value.splice(filteredIdx, 1);
      }
    } else {
      item.recordMode = 3;
      modifiedCompetitorMaterials.value.push(item);
    }
  });
}

async function onSaveForm() {
  const res = await addOrUpdateCompetitorMaterials(modifiedCompetitorMaterials.value);
  if (res) {
    modifiedCompetitorMaterials.value = [];
    const updatedCompetitorMaterials = await getCompetitorMaterialsWithConditions({
      competitorId: selectedCompetitor.value?.id,
      materialId: selectedMaterial.value?.id,
    });

    if (updatedCompetitorMaterials) {
      filteredCompetitorMaterials.value = updatedCompetitorMaterials.data.map(
        (item: CompetitorMaterial) => {
          return { ...item, recordMode: undefined };
        }
      );
    }
  }
}

const confirmDrawerFormActive = ref(false);
const router = useRouter();
let isGoBack = false;

function clearFormState(goBack = false) {
  selectedCompetitor.value = undefined;
  modifiedCompetitorMaterials.value = [];
  competitorMaterials.value = [];
  filteredCompetitorMaterials.value = [];
  isDisabledCompetitorMaterial.value = true;
  confirmDrawerFormActive.value = false;

  if (goBack) {
    router.push({ name: 'competitor-materials' });
  }
}

function onCancelForm() {
  if (modifiedCompetitorMaterials.value.length > 0) {
    confirmDrawerFormActive.value = true;
    isGoBack = false;
  } else clearFormState();
}

function onGoBack() {
  if (modifiedCompetitorMaterials.value.length > 0) {
    confirmDrawerFormActive.value = true;
    isGoBack = true;
  } else clearFormState(true);
}
function onDiscardFormChanges() {
  clearFormState(isGoBack);
  isGoBack = false;
}

watchEffect(() => {
  const processedIds = new Set(filteredCompetitorMaterials.value.map((item) => item.id));
  modifiedCompetitorMaterials.value.forEach((item) => {
    switch (item.recordMode) {
      case 1: {
        if (!processedIds.has(item.id) && selectedCompetitor.value?.id === item.competitorId) {
          filteredCompetitorMaterials.value.push({ ...item });
          processedIds.add(item.id);
        }
        break;
      }

      case 2: {
        const idx = filteredCompetitorMaterials.value.findIndex(
          (existingItem) => existingItem.id === item.id
        );
        if (idx !== -1) {
          filteredCompetitorMaterials.value[idx] = {
            ...filteredCompetitorMaterials.value[idx],
            ...item,
          };
        }
        break;
      }

      case 3: {
        const idx = filteredCompetitorMaterials.value.findIndex(
          (existingItem) => existingItem.id === item.id
        );
        if (idx !== -1) {
          filteredCompetitorMaterials.value.splice(idx, 1);
        }
        break;
      }

      default:
        break;
    }
  });
});

function onSelectCompetitorMaterial(item: CompetitorMaterial) {
  onOpenModal(item);
}
</script>
